import { useEffect, useState } from 'react';
import { useStore } from 'zustand';

import type { FormDraft, FormDraftStore } from '@/shared/store/formDrafts';
import { useBeforeUnloadGuard } from './useBeforeUnloadGuard';

/**
 * The draft a form opens with: read once, when the form mounts, and only when
 * drafts apply to it (a create form, not an edit). A stale draft is dropped by
 * the store rather than offered (spec 124 R4).
 */
export function useRestoredDraft<V>(
  store: FormDraftStore<V>,
  scope: string,
  enabled: boolean,
): FormDraft<V> | undefined {
  const [restored] = useState(() =>
    enabled ? store.getState().takeDraft(scope) : undefined,
  );
  return restored;
}

/**
 * Keeps the draft current while the person types, and guards the page while
 * one exists (spec 123 FR-012/FR-015, spec 124 FR-009/FR-012).
 *
 * Written a beat after each change, and only once something has changed: an
 * untouched form keeps nothing. Not an interval — one deferred write per burst
 * of typing, cancelled by the next. `watched` is whatever the caller watches
 * of its form; its identity changing is the "something changed" signal.
 */
export function useDraftAutosave<V>({
  store,
  scope,
  enabled,
  isDirty,
  watched,
  read,
}: {
  store: FormDraftStore<V>;
  scope: string;
  enabled: boolean;
  isDirty: boolean;
  watched: unknown;
  read: () => V;
}): { hasDraft: boolean } {
  const saveDraft = useStore(store, (state) => state.saveDraft);
  useEffect(() => {
    if (!enabled || !isDirty) return;
    const timer = setTimeout(() => {
      saveDraft(scope, read());
    }, 250);
    return () => {
      clearTimeout(timer);
    };
  }, [enabled, isDirty, watched, scope, saveDraft, read]);

  const hasDraft = useStore(
    store,
    (state) => enabled && Boolean(state.drafts[scope]),
  );
  useBeforeUnloadGuard(hasDraft);
  return { hasDraft };
}
